import { useState, useRef, useEffect } from 'react'
import { useTradeDefaults } from './TradeDefaultsContext'

export default function TradeDefaultsMenu() {
  const { defaults, setDefaults, resetDefaults } = useTradeDefaults()
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)

  // Opened by the chat settings runner (utils/grokActions) via the event bus.
  useEffect(() => {
    const onOpen = () => setOpen(true)
    window.addEventListener('cheddar:open-trade-defaults', onOpen)
    return () => window.removeEventListener('cheddar:open-trade-defaults', onOpen)
  }, [])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleChange = (key, value) => {
    const num = parseFloat(value)
    if (!isNaN(num) && num >= 0) {
      setDefaults({ [key]: num })
    }
  }

  return (
    <div className="trade-defaults-wrapper" ref={menuRef}>
      <button
        className={`trade-defaults-toggle ${open ? 'active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Order Defaults"
      >
       <img src="/scroll.png" alt="Wallet logo" className="wallet-logo" /> 
      </button>

      {open && (
        <div className="trade-defaults-menu">
          <div className="trade-defaults-header">
            <span>Order Defaults</span>
            <button className="trade-defaults-reset" onClick={resetDefaults} title="Reset to defaults">↺</button>
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Market Buy Amount (SOL)</div>
            <input
              type="number"
              className="trade-defaults-input"
              value={defaults.buyAmountSol}
              min="0.001"
              step="0.05"
              onChange={(e) => handleChange('buyAmountSol', e.target.value)}
              data-setting-id="trade.buyAmountSol"
            />
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Market Sell Amount (%)</div>
            <input
              type="number"
              className="trade-defaults-input"
              value={defaults.sellPercentage}
              min="1"
              max="100"
              step="1"
              onChange={(e) => handleChange('sellPercentage', e.target.value)}
              data-setting-id="trade.sellPercentage"
            />
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Slippage (%)</div>
            <input
              type="number"
              className="trade-defaults-input"
              value={(defaults.slippageBps / 100).toFixed(1)}
              min="0.1"
              max="50"
              step="0.5"
              onChange={(e) => handleChange('slippageBps', parseFloat(e.target.value) * 100)}
              data-setting-id="trade.slippageBps"
            />
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Priority Fee (SOL)</div>
            <input
              type="number"
              className="trade-defaults-input"
              value={(defaults.priorityFeeLamports / 1e9).toFixed(6)}
              min="0"
              step="0.00005"
              onChange={(e) => handleChange('priorityFeeLamports', parseFloat(e.target.value) * 1e9)}
              data-setting-id="trade.priorityFeeLamports"
            />
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Jito Tip (SOL)</div>
            <input
              type="number"
              className="trade-defaults-input"
              value={(defaults.jitoTipLamports / 1e9).toFixed(6)}
              min="0"
              step="0.00005"
              onChange={(e) => handleChange('jitoTipLamports', parseFloat(e.target.value) * 1e9)}
              data-setting-id="trade.jitoTipLamports"
            />
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Order venue</div>
            <div style={{ display: 'flex', gap: '4px' }} data-setting-id="trade.orderVenue">
              {['jupiter', 'meteora'].map(v => (
                <button
                  key={v}
                  type="button"
                  className={`trade-defaults-input ${defaults.orderVenue === v ? 'active' : ''}`}
                  style={{
                    flex: 1, cursor: 'pointer',
                    fontWeight: (defaults.orderVenue || 'jupiter') === v ? 700 : 400,
                    outline: (defaults.orderVenue || 'jupiter') === v ? '1px solid #46d41f' : 'none',
                  }}
                  onClick={() => setDefaults({ orderVenue: v })}
                  title={v === 'meteora'
                    ? 'Market: direct swap on the deepest Meteora pool. Limit: native DLMM limit orders.'
                    : 'Market: Jupiter Ultra. Limit: Jupiter Trigger.'}
                >
                  {v === 'jupiter' ? 'Jupiter' : 'Meteora'}
                </button>
              ))}
            </div>
          </div>

          <div className="trade-defaults-divider" style={{ borderTop: '1px solid #2a2a40', margin: '8px 0', paddingTop: '4px' }}>
            <div className="trade-defaults-label" style={{ color: '#46d41f', fontSize: '10px', letterSpacing: '0.08em' }}>FUTURES</div>
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Order Qty (base coin)</div>
            <input
              type="number"
              className="trade-defaults-input"
              value={defaults.futuresQty}
              min="0.0001"
              step="0.001"
              onChange={(e) => handleChange('futuresQty', e.target.value)}
              data-setting-id="trade.futuresQty"
            />
          </div>

          <div className="trade-defaults-section">
            <div className="trade-defaults-label">Leverage</div>
            <input
              type="number"
              className="trade-defaults-input"
              value={defaults.futuresLeverage}
              min="1"
              max="50"
              step="1"
              onChange={(e) => handleChange('futuresLeverage', e.target.value)}
              data-setting-id="trade.futuresLeverage"
            />
          </div>

          <div className="trade-defaults-summary">
            <span className="summary-buy">▲ {defaults.buyAmountSol} SOL</span>
            <span className="summary-sell">▼ {defaults.sellPercentage}%</span>
            <span className="summary-slip">{(defaults.slippageBps / 100).toFixed(1)}% slip · {defaults.orderVenue === 'meteora' ? 'Meteora' : 'Jupiter'}</span>
            <span className="summary-futures" style={{ color: '#46d41f', fontSize: '10px' }}>{defaults.futuresQty} @ {defaults.futuresLeverage}x</span>
          </div>
        </div>
      )}
    </div>
  )
}
